import "./Navigation.scss"

import {
  AccountStatusLabel,
  BannerNotification,
  BannerVariant,
  EmailStatusLabel,
  FooterVariant,
  NavigationItem,
  Search,
  SeatSelect,
  SvgIcon,
} from "@skylead/component-library"
import classNames from "classnames"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, useLocation, useNavigate } from "react-router-dom"

import { ACTIVE_STATUS_IDS } from "../../../constants/account-statuses"
import { DEFAULT_PROFILE_IMAGE } from "../../../constants/chat-constants"
import settings from "../../../constants/settings"
import * as appActions from "../../../redux/actions"
import { store } from "../../../redux/store"
import { onLabelClick, openEmailProviderConnect } from "../../../utils/account-utils"
import { getLogo } from "../../../utils/app-utils"
import { useConfig, useGlobalTranslation, useTooltip } from "../../../utils/hook"
import { isInFreeTrial } from "../../../utils/payment-utils"
import { hasPermission, PERMISSIONS } from "../../../utils/team-management/team-management-utils"
import { validateNewTeamName, validateNoEmojis } from "../../../utils/validation/validation-utils"
import Button, { ButtonVariant } from "../../atoms/Button"
import FreeTrialBanner from "../../atoms/FreeTrialBanner"
import LangText from "../../atoms/LangText"
import { ModalVariant } from "../../atoms/Modal"
import SeatSelectList from "../../molecules/SeatSelectList"
import InputGroup from "../InputGroup"
import MasterLoginTeamsList from "../MasterLoginTeamsList"
import TopbarNotifications from "../TopbarNotifications"

export const NavigationVariant = {
  SEAT: "seat",
  TEAM: "team",
}

const Navigation = ({
  navigationItems,
  navbarWidth = 288,
  setNavbarWidth = () => {},
  type = NavigationVariant.SEAT,
  loadingSeats = false,
  setOnlyBuySeat = () => {},
  showTransferSeatsModal,
}) => {
  const { tt } = useGlobalTranslation()
  const config = useConfig()
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const {
    accountID,
    user,
    teamName,
    teamAccounts,
    logo,
    teamAccountsSumCount,
    teamAccountsCount,
    userTeams,
    userTeamsCount,
    userTeamsSumCount,
    selectedTeam,
    userData,
    accountStatuses,
    proxyCountries,
    allSubscriptions,
    pricingPlans,
  } = useSelector(state => ({
    accountID: state.account.activeAccountID,
    user: state.user.profile,
    teamName: state.teamManagement?.selectedTeam?.teamName,
    teamAccounts: state.teamManagement.teamAccounts.items,
    logo: state.forms.formData.logo,
    teamAccountsSumCount: state.teamManagement.teamAccounts.sumCount,
    teamAccountsCount: state.teamManagement.teamAccounts.count,
    userTeams: state.teamManagement.userTeams.items,
    userTeamsCount: state.teamManagement.userTeams.count,
    userTeamsSumCount: state.teamManagement.userTeams.sumCount,
    selectedTeam: state.teamManagement.selectedTeam,
    userData: state.user.profile,
    accountStatuses: state.app.accountStatuses,
    proxyCountries: state.app.proxyCountries,
    allSubscriptions: state.payment.allSubscriptions,
    pricingPlans: state.payment.pricingPlans,
  }))

  const currentTeamAccount = teamAccounts.find(account => account.id === Number(accountID)) || {}
  const isSeatNav = type === NavigationVariant.SEAT
  const isTeamNav = type === NavigationVariant.TEAM
  const userID = user.id
  const rootUrl = `/users/${userID}/`
  const isExpanded = isTeamNav || navbarWidth !== 84

  const [expanded, setExpanded] = useState(isExpanded)
  const [isSeatSelectOpen, setIsSeatSelectOpen] = useState(false)
  const [showSearch, setShowSearch] = useState(false)
  const [tooltip, setTooltip, closeTooltip] = useTooltip(10, 0)
  const [seatSearch, setSeatSearch] = useState("")
  const [teamSearch, setTeamSearch] = useState("")
  const [lastQueriedSearch, setLastQueriedSearch] = useState("")
  const [loadingTeams, setLoadingTeams] = useState(false)
  const [currentPage, setCurrentPage] = useState(0)
  const [inFreeTrial, setInFreeTrial] = useState(false)
  const [showBanner, setShowBanner] = useState(false)

  const [currentAccount, setCurrentAccount] = useState({
    linkedinUser: {},
  })

  const linkedinAndEmailStatusesClass = classNames("linkedin-and-email-statuses", {
    "linkedin-and-email-statuses-collapsed": !isExpanded,
  })

  const showLinkedInStatus = useMemo(() => {
    return (
      isSeatNav &&
      currentAccount.email &&
      currentAccount.accountGlobalStatusId >= 0 &&
      !ACTIVE_STATUS_IDS.includes(currentAccount.accountGlobalStatusId) &&
      hasPermission(PERMISSIONS.LINKEDIN_INTEGRATIONS_EDIT)
    )
  }, [isSeatNav, currentAccount])

  const showEmailStatus = useMemo(() => {
    return (
      isSeatNav &&
      currentTeamAccount.totalMailboxes !== currentTeamAccount.activeMailboxes &&
      currentAccount.emailOauths &&
      hasPermission(PERMISSIONS.EMAIL_SETTINGS_EDIT)
    )
  }, [isSeatNav, currentAccount, currentTeamAccount])

  useEffect(() => {
    dispatch(appActions.setSelectedTeamAccount(currentTeamAccount))
    dispatch(
      appActions.updateFormField(
        "linkedin-subscription",
        currentTeamAccount.linkedinSubscriptionId,
      ),
    )

    if (currentTeamAccount) {
      setCurrentAccount(currentTeamAccount)
    }
  }, [teamAccounts, accountID])

  useEffect(() => {
    setInFreeTrial(isInFreeTrial(allSubscriptions))
  }, [allSubscriptions])

  useEffect(() => {
    if (!pricingPlans?.length) {
      dispatch(appActions.getPaymentPlans())
    }

    if (!proxyCountries?.length) {
      dispatch(appActions.getProxyCountries())
    }
  }, [])

  const profileImage = (currentAccount.linkedinUser || {}).picture || DEFAULT_PROFILE_IMAGE

  const submitSearchAccounts = async () => {
    const searchAccounts = store.getState().forms.formData["search-account-switcher-list"]
    if (!searchAccounts && !teamAccountsCount) {
      await dispatch(appActions.getTeamAccounts(searchAccounts))
    } else if (searchAccounts) {
      await dispatch(appActions.getTeamAccounts(searchAccounts))
    }
  }

  const submitTeamSearch = async () => {
    const getSearchResult = async () => {
      setLoadingTeams(true)
      await setCurrentPage(0)
      await dispatch(
        appActions.getTeamsForUser(
          teamSearch?.trim(),
          settings.whitelabelAccountsLimit,
          0,
          navigate,
        ),
      )
      setLastQueriedSearch(teamSearch)
      setLoadingTeams(false)
    }
    if (!loadingTeams) {
      if (teamSearch || userTeamsCount || (!teamSearch && !userTeamsCount)) {
        await getSearchResult()
      }
    }
  }

  const hasMore = useMemo(() => {
    return userTeamsCount > currentPage * settings.whitelabelAccountsLimit
  }, [userTeamsCount, currentPage])

  const loadMore = async () => {
    if (!loadingTeams && userTeamsCount > userTeams.length) {
      const search = lastQueriedSearch
      await setLoadingTeams(true)
      await dispatch(
        appActions.getTeamsForUser(
          search,
          settings.whitelabelAccountsLimit,
          (currentPage + 1) * settings.whitelabelAccountsLimit,
          navigate,
        ),
      )
      await setCurrentPage(currentPage + 1)
      await setLoadingTeams(false)
    }
  }

  const onToggle = () => {
    setExpanded(!expanded)
    if (expanded) {
      localStorage.setItem("sidebarWidth", JSON.stringify(84))
      setNavbarWidth(84)
    } else {
      localStorage.setItem("sidebarWidth", JSON.stringify(288))
      setNavbarWidth(288)
    }
  }

  const toggleSearch = () => {
    setShowSearch(!showSearch)
  }

  const openSeatSelect = () => {
    setIsSeatSelectOpen(true)
  }

  const handleCreateCompany = async () => {
    dispatch(appActions.clearFormErrors())
    const allErrors = validateNewTeamName({ teamName })
    const emojiErrors = validateNoEmojis({ id: "teamName", value: teamName })

    if (allErrors || emojiErrors) {
      dispatch(appActions.setFormErrors(allErrors || emojiErrors))
    } else {
      const success = await dispatch(appActions.createTeam(tt, navigate))
      if (success) {
        dispatch(appActions.closeModal())
        dispatch(appActions.clearForm())
      }
    }
  }

  const createTeamModal = () => {
    dispatch(appActions.clearForm())
    dispatch(
      appActions.showModal(
        ModalVariant.SMALL,
        tt("create-team"),
        <InputGroup field="teamName" label={tt("team-name")} type="text" />,
        FooterVariant.TRANSACTIONAL,
        { label: tt("create"), onClick: handleCreateCompany },
        { label: tt("cancel") },
        undefined,
        true,
      ),
    )
  }

  const selectTeam = team => {
    if (!loadingSeats) {
      setOnlyBuySeat(false)
      dispatch(appActions.setSelectedTeam(team))
      dispatch(appActions.updateFormFields({ searchAccounts: undefined }))

      const newLocation = `/users/${userData.id}`
      if (location?.pathname !== newLocation) {
        navigate(newLocation)
      }
    }
  }

  const navigationHeaderClass = classNames("navigation-header", {
    "is-seat-nav": isSeatNav,
  })

  const shouldRenderSeatWrapperStatus =
    showLinkedInStatus ||
    showEmailStatus ||
    (isSeatNav && showBanner) ||
    inFreeTrial ||
    !userData.emailConfirmed

  const navigationWrapperClassnames = classNames("navigation-wrapper", {
    collapsed: !expanded,
    "navigation-wrapper-master-login": isTeamNav,
  })

  return (
    <div className={navigationWrapperClassnames}>
      {tooltip}
      <div className={navigationHeaderClass}>
        <div className="navigation-logo">
          <Link to={rootUrl}>
            <img src={logo || getLogo(config)} alt="header-logo" />
          </Link>
        </div>

        {isSeatNav && (
          <div
            onMouseEnter={e =>
              setTooltip({
                event: e,
                caretLeft: true,
                text: expanded ? tt("collapse") : tt("expand"),
                topPos: -5,
                leftPos: expanded ? -50 : -50,
              })
            }
            onMouseLeave={closeTooltip}
          >
            <NavigationItem
              icon={expanded ? "menu-collapse-28" : "menu-expand-28"}
              type="top-bar"
              onClick={onToggle}
            />
          </div>
        )}
      </div>
      {isSeatNav && (
        <div className="navigation-container">
          <SeatSelect
            seatName={currentAccount.fullName}
            teamName={teamName}
            compact={!expanded}
            imgSource={profileImage}
            onClick={openSeatSelect}
          />

          {isSeatSelectOpen && (
            <SeatSelectList
              submitSearchAccounts={submitSearchAccounts}
              seatSearch={seatSearch}
              setSeatSearch={setSeatSearch}
              teamAccountsSumCount={teamAccountsSumCount}
              setIsSeatSelectOpen={setIsSeatSelectOpen}
              setTooltip={setTooltip}
              closeTooltip={closeTooltip}
            />
          )}
        </div>
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          flex: 1,
          overflow: "auto",
          gap: "var(--space-06)",
        }}
      >
        {shouldRenderSeatWrapperStatus && (
          <div className="seat-wrapper-status">
            {(showLinkedInStatus || showEmailStatus) && (
              <div className={linkedinAndEmailStatusesClass}>
                {showLinkedInStatus && (
                  <div className="linkedin-and-email-statuses__row">
                    {isExpanded && (
                      <div className="small linkedin-and-email-status-prefix">
                        <LangText langKey="label.linkedin" />
                      </div>
                    )}
                    <AccountStatusLabel
                      tt={tt}
                      onClick={event =>
                        onLabelClick(
                          tt,
                          event,
                          currentAccount,
                          accountStatuses,
                          true,
                          userID,
                          undefined,
                          proxyCountries,
                          undefined,
                          undefined,
                          navigate,
                        )
                      }
                      small
                      status={currentAccount.email ? currentAccount.accountGlobalStatusId : -1}
                      accountStatuses={accountStatuses}
                      isHeaderMenu={!isExpanded}
                      isSuspended={currentAccount?.suspended}
                    />
                  </div>
                )}
                {showEmailStatus && (
                  <div className="linkedin-and-email-statuses__row">
                    {isExpanded && (
                      <div className="small linkedin-and-email-status-prefix">
                        <LangText langKey="label.email" />
                      </div>
                    )}
                    <EmailStatusLabel
                      tt={tt}
                      onClick={event =>
                        openEmailProviderConnect(
                          tt,
                          event,
                          userID,
                          currentAccount,
                          undefined,
                          undefined,
                          selectedTeam?.teamId,
                        )
                      }
                      small
                      currentSeat={currentAccount}
                      isHeaderMenu={!isExpanded}
                    />
                  </div>
                )}
              </div>
            )}
            {expanded && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-04)",
                }}
              >
                {isSeatNav && (
                  <TopbarNotifications
                    sidebar
                    addMarginBottom={!(inFreeTrial || !userData.emailConfirmed)}
                    setShowBanner={setShowBanner}
                  />
                )}
                {(inFreeTrial || !userData.emailConfirmed) && (
                  <div className="banners-wrapper">
                    {!userData.emailConfirmed && (
                      <BannerNotification
                        ariaLabel="Please confirm your email"
                        type={BannerVariant.CONFIRMATION}
                        label={tt("navigation.banner.email.label")}
                        bannerClick={() => dispatch(appActions.resendConfirmationEmail(tt))}
                      />
                    )}

                    <FreeTrialBanner userID={userID} allSubscriptions={allSubscriptions} />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
        {isTeamNav && (
          <div className="navigation-team-header">
            <div className="navigation-item large">
              <SvgIcon icon="teams-28" className="navigation-item-icon" />
              <span>
                <LangText langKey="teams" />
              </span>
            </div>
            <Button variant={ButtonVariant.GHOST} icon="search-20" onClick={toggleSearch} />
            <Button variant={ButtonVariant.GHOST} icon="plus-20" onClick={createTeamModal} />
          </div>
        )}
        {showSearch && (
          <div className="navigation-search">
            <Search
              autoFocus
              placeholder={tt("search-team-placeholder")}
              value={teamSearch}
              onEnter={submitTeamSearch}
              onChange={setTeamSearch}
            />
          </div>
        )}
        {isTeamNav && (
          <MasterLoginTeamsList
            userTeams={userTeams}
            userData={userData}
            selectedTeam={selectedTeam}
            selectTeam={selectTeam}
            userTeamsSumCount={userTeamsSumCount}
            loadingTeams={loadingTeams}
            loadingSeats={loadingSeats}
            loadMore={loadMore}
            hasMore={hasMore}
            showTransferSeatsModal={showTransferSeatsModal}
            isTeamsTabActive={navigationItems.filter(item => item.isActive)?.length === 0}
          />
        )}

        <div className="navigation-menu">
          {navigationItems.map(item => {
            if (!item.show) {
              return null
            }

            if (item.linkTo) {
              return (
                <Link to={item.linkTo} onClick={item.onClick}>
                  <NavigationItem
                    icon={item.icon}
                    label={item.label}
                    active={item.isActive}
                    type={expanded ? "drawer" : "rail"}
                    tooltip={expanded ? undefined : item.tooltip}
                  />
                </Link>
              )
            }

            return (
              <NavigationItem
                icon={item.icon}
                label={item.label}
                active={item?.isActive}
                type={expanded ? "drawer" : "rail"}
                onClick={item.onClick}
              />
            )
          })}
        </div>
      </div>
    </div>
  )
}

// Navigation.propTypes = {
//   navigationItems: PropTypes.instanceOf(Array).isRequired,
//   navbarWidth: PropTypes.number,
//   setNavbarWidth: PropTypes.func,
//   type: PropTypes.string,
//   loadingSeats: PropTypes.bool,
//   setOnlyBuySeat: PropTypes.func,
// }

export default Navigation
